import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { COLOR_SCHEMES } from '../data/colorSchemes';

const defaultSettings = {
  backgroundImageUrl: null,
  backgroundLoginEnabled: true,
  backgroundDashboardEnabled: true,
  backgroundKartotekaEnabled: true,
  backgroundResultsEnabled: true,
  backgroundTestEnabled: true,
  backgroundAdminEnabled: true,
};

const ThemeContext = createContext({
  colorSet: 'default',
  colors: COLOR_SCHEMES[0].colors,
  loading: true,
  ...defaultSettings,
  setColorSet: () => {},
  setSettings: () => {},
});

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const userRef = useRef(user?.username ?? null);
  userRef.current = user?.username ?? null;

  const [colorSet, setColorSet] = useState('default');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(defaultSettings);

  const scheme = COLOR_SCHEMES.find((s) => s.id === colorSet) || COLOR_SCHEMES[0];
  const colors = scheme.colors;

  // Ładujemy ustawienia tylko dla zalogowanego użytkownika. Stosujemy wynik tylko gdy nadal ten sam user (unikanie nadpisania przy race).
  useEffect(() => {
    const forUser = user?.username ?? null;
    (async () => {
      try {
        const url = forUser
          ? `/.netlify/functions/get-settings?username=${encodeURIComponent(forUser)}`
          : '/.netlify/functions/get-settings';
        const res = await fetch(url);
        const data = await res.json();
        if (userRef.current !== forUser) return;
        const id = data.color_set || 'default';
        if (COLOR_SCHEMES.some((s) => s.id === id)) {
          setColorSet(id);
        }
        const def = (v) => v !== false;
        setSettings({
          backgroundImageUrl: data.background_image_url || null,
          backgroundLoginEnabled: def(data.background_login_enabled),
          backgroundDashboardEnabled: def(data.background_dashboard_enabled),
          backgroundKartotekaEnabled: def(data.background_kartoteka_enabled),
          backgroundResultsEnabled: def(data.background_results_enabled),
          backgroundTestEnabled: def(data.background_test_enabled),
          backgroundAdminEnabled: def(data.background_admin_enabled),
        });
      } catch {
        if (userRef.current !== forUser) return;
      } finally {
        if (userRef.current === forUser) setLoading(false);
      }
    })();
  }, [user?.username]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', colorSet);
  }, [colorSet]);

  const updateSettings = (updates) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  return (
    <ThemeContext.Provider
      value={{
        colorSet,
        colors,
        loading,
        setColorSet,
        ...settings,
        setSettings: updateSettings,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
